import { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth";
import { api, uploadFile, coverUri } from "../lib/api";
import { initials } from "../lib/format";
import { Button } from "../components/ui";
import { Icon } from "../components/icons";
import { adminNavFor, roleLabel } from "../lib/permissions";

function MenuRow({ to, onClick, icon, label, testid }) {
  const inner = (
    <>
      <div style={{ width: 40, height: 40, borderRadius: 999, background: "var(--surface-3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name={icon} size={18} color="var(--brand-primary)" />
      </div>
      <span style={{ flex: 1, fontFamily: "var(--f-medium)", fontSize: 15 }}>{label}</span>
      <Icon name="chevron-forward" size={18} color="var(--muted)" />
    </>
  );
  const style = { display: "flex", alignItems: "center", gap: 12, padding: 12, textDecoration: "none", color: "inherit", cursor: "pointer", border: "none", background: "var(--surface-2)", borderRadius: "var(--r-md)", width: "100%" };
  return to ? <Link to={to} data-testid={testid} style={style}>{inner}</Link> : <button onClick={onClick} data-testid={testid} style={style}>{inner}</button>;
}

export default function Perfil() {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const adminNav = adminNavFor(user);
  const photo = coverUri(user?.photo_url);

  const changePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadFile("/me/photo", file);
      updateUser({ ...user, photo_url: res.photo_url ?? res.url ?? user.photo_url });
      qc.invalidateQueries({ queryKey: ["home"] });
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async () => {
    setBusy(true);
    try {
      await api("/me/photo", { method: "DELETE" });
      updateUser({ ...user, photo_url: null });
      qc.invalidateQueries({ queryKey: ["home"] });
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <>
      <div className="page-header">
        <h1 className="t-h1">Perfil</h1>
      </div>
      <div className="page ulc-fade" style={{ maxWidth: 720 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
          <button data-testid="profile-photo-button" onClick={() => fileRef.current?.click()} className="avatar" style={{ width: 84, height: 84, fontSize: 30, position: "relative", border: "none", cursor: "pointer" }}>
            {photo ? <img data-testid="profile-avatar" src={photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(user?.name)}
            <span style={{ position: "absolute", bottom: 0, right: 0, width: 28, height: 28, borderRadius: 999, background: "var(--brand-primary)", display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid var(--surface)" }}>
              <Icon name="pencil" size={13} color="var(--on-brand)" />
            </span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={changePhoto} data-testid="profile-photo-change" />
          <div style={{ minWidth: 0 }}>
            <div data-testid="profile-name" style={{ fontFamily: "var(--f-medium)", fontSize: 22 }}>{user?.name}</div>
            <div data-testid="profile-email" style={{ color: "var(--on-surface-3)", fontSize: 14 }}>{user?.email}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              {user?.voice ? <span className="pill doing" data-testid="profile-voice">{user.voice.toUpperCase()}</span> : null}
              <span className="pill todo" data-testid="profile-role">{roleLabel(user)}</span>
              {user?.is_singer === false ? <span className="pill" data-testid="profile-not-singer">NÃO CANTA</span> : null}
            </div>
          </div>
        </div>
        {photo ? (
          <button data-testid="profile-photo-remove" onClick={removePhoto} disabled={busy} style={{ background: "none", border: "none", color: "var(--muted)", fontFamily: "var(--f-medium)", fontSize: 11, letterSpacing: 1, cursor: "pointer", padding: "4px 0", marginBottom: 12 }}>
            REMOVER FOTOGRAFIA
          </button>
        ) : null}

        <div className="t-section" style={{ marginTop: 20, marginBottom: 10 }}>Conta</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <MenuRow to="/attendance" icon="stats" label="A minha assiduidade" testid="profile-attendance-link" />
          <MenuRow to="/calendar" icon="calendar-outline" label="Agenda" testid="profile-calendar-link" />
          <MenuRow to="/password" icon="key" label="Alterar palavra-passe" testid="profile-password-link" />
        </div>

        {adminNav.length ? (
          <>
            <div className="t-section" style={{ marginTop: 24, marginBottom: 10 }}>Administração</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {adminNav.map((n) => <MenuRow key={n.to} to={n.to} icon={n.icon} label={n.label} testid={`${n.testid}-profile`} />)}
            </div>
          </>
        ) : null}

        <div style={{ marginTop: 28 }}>
          <Button variant="danger" onClick={doLogout} data-testid="logout-button" icon="logout">TERMINAR SESSÃO</Button>
        </div>
      </div>
    </>
  );
}
